import { Ellipsis, MessageSquare, Pencil } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { hasFeature } from '@/services/featureService';
import { Feature } from '@/utils/constant';
import { canAssignDdiWorkOrderToMe, isAdminOrSupervisor, isDueDiligenceTeamMember } from '@/services/userService';
import { useAssignDdiWorkOrder } from '@/services/dueDiligenceService';
import { useUserStore } from '@/stores/user';
import type { DdiWorkOrderListItem } from '@/types/dueDiligence.types';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from '@/components/ui/use-toast';
import { displayError } from '@/lib/error';

type DDIRowActionsProps = {
  row: DdiWorkOrderListItem;
  navigate: ReturnType<typeof useNavigate>;
  onOpenComments: (workOrderId: string) => void;
  commentCount: number;
};

export const DDIRowActions = ({ row, navigate, onOpenComments, commentCount }: DDIRowActionsProps) => {
  const queryClient = useQueryClient();
  const loggedInUser = useUserStore((state) => state.user);
  const assignMutation = useAssignDdiWorkOrder();

  const canEdit =
    hasFeature(Feature.DUE_DILIGENCE_WORK_ORDER_EDIT) &&
    (isDueDiligenceTeamMember(loggedInUser) || isAdminOrSupervisor(loggedInUser)) &&
    row.status !== 'CANCELED';

  // Mirrors checkDdiAssignPermission on the backend: only DDI-team members, supervisors,
  // or admins may assign at all (self-assignment here always satisfies "assign to self").
  const canAssignAtAll =
    hasFeature(Feature.DUE_DILIGENCE_WORK_ORDER_ASSIGN) && canAssignDdiWorkOrderToMe(loggedInUser);
  // Mirrors assertWorkOrderAssignable on the backend: CANCELED and BILLED work
  // orders cannot be assigned regardless of caller eligibility.
  const isRowAssignable = row.status !== 'CANCELED' && row.status !== 'BILLED';
  const canViewComments = isDueDiligenceTeamMember(loggedInUser) || isAdminOrSupervisor(loggedInUser);

  const handleAssignMe = () => {
    if (!loggedInUser?.userId || !isRowAssignable) return;

    assignMutation.mutate(
      { workOrderId: row.id, userId: loggedInUser.userId },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({ queryKey: ['ddi-work-orders'] });
          toast({
            variant: 'default',
            description: 'Work order assigned to you successfully.',
            duration: 3000,
          });
        },
        onError: (error) => {
          if (axios.isAxiosError(error) && error.response?.data) {
            displayError(error.response.data, 5000);
          } else {
            toast({
              variant: 'destructive',
              description: 'Failed to assign work order to you.',
              duration: 5000,
            });
          }
        },
      }
    );
  };

  return (
    <div className="action flex items-center gap-4 me-5" onClick={(e) => e.stopPropagation()}>
      {canEdit && (
        <div
          role="button"
          tabIndex={0}
          title="Edit Work Order"
          aria-label="Edit Work Order"
          className="cursor-pointer text-primary"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/due-diligence/${row.id}/update`);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.stopPropagation();
              navigate(`/due-diligence/${row.id}/update`);
            }
          }}
        >
          <Pencil className="h-5 w-5" />
        </div>
      )}
      {canViewComments && (
        <button
          type="button"
          title="View Comments"
          className="relative cursor-pointer text-primary"
          onClick={(e) => {
            e.stopPropagation();
            onOpenComments(row.id);
          }}
        >
          <MessageSquare className="h-5 w-5" />
          {commentCount > 0 && (
            <span className="absolute -top-2 -right-2 flex items-center justify-center min-w-[16px] h-[16px] px-1 text-[8px] font-semibold text-white bg-primary rounded-full">
              {commentCount}
            </span>
          )}
        </button>
      )}
      {canAssignAtAll && (
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <div role="button" title="More" className="cursor-pointer text-primary">
              <Ellipsis className="h-5 w-5" />
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="bg-white border-[1px] border-bordercolor z-[1000] rounded-md max-h-[300px] overflow-auto custom-scrollbar"
          >
            {canAssignAtAll &&
              (isRowAssignable ? (
                <DropdownMenuItem
                  className="!opacity-100 !font-semibold"
                  disabled={assignMutation.isPending}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAssignMe();
                  }}
                >
                  Assign Me
                </DropdownMenuItem>
              ) : (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div>
                        <DropdownMenuItem
                          disabled
                          className="!opacity-100 !font-semibold text-placeholder"
                          onSelect={(e) => e.preventDefault()}
                        >
                          Assign Me
                        </DropdownMenuItem>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      Canceled or Billed work orders cannot be assigned.
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
};
